import { getPostImage } from 'src/client/publication';
import { Loading } from 'src/components';
import { PostType as MediaType } from 'src/hook/usePosts';
import { PublicationEndpoints } from 'src/client/publication/constants';
import useRequest from 'src/hook/useRequest';
import { useUser } from 'src/hook/useUser';
import { AuthStatus } from '@hotmart/space-lib/dist/enum';
import { Dimensions, MediaDisplayFormat, PostMediaPreviewProps } from './types';
import { ThumbnailImage, ThumbnailVideo } from './components';
import { REQUEST_CONFIG } from './postMediaThumbnail.constants';

const PostMediaPreview = ({ id, mediaType, displayFormat, className }: PostMediaPreviewProps) => {
  const { authStatus } = useUser();

  const isUnauthenticated = authStatus === AuthStatus.UNAUTHENTICATED;
  const url = isUnauthenticated
    ? PublicationEndpoints.getPublicPostImage(id)
    : PublicationEndpoints.getPostImage(id);

  const dimensionsObject: Dimensions = {
    [MediaDisplayFormat.MiniSquare]: {
      width: 64,
      height: 64
    },
    [MediaDisplayFormat.Square]: {
      width: 100,
      height: 100
    }
  };

  const dimensions = displayFormat ? dimensionsObject[displayFormat] : undefined;
  const cacheKey = `${url}-${Object.values(dimensions || {}).toString()}`;

  const {
    data: src,
    isError,
    isLoading
  } = useRequest(
    cacheKey,
    /* istanbul ignore next */
    () => getPostImage({ publicationId: id, imageDimension: dimensions, isUnauthenticated }),
    REQUEST_CONFIG
  );

  if (isLoading) {
    return (
      <div className="_w-full _h-full">
        <Loading full />
      </div>
    );
  }

  if (isError) {
    return null;
  }

  switch (mediaType) {
    case MediaType.IMAGE:
      return <ThumbnailImage src={src as string} className={className} dataTestId="image-post" />;

    case MediaType.VIDEO:
      return <ThumbnailVideo src={src as string} className={className} dataTestId="video-post" />;

    default:
      return null;
  }
};

export default PostMediaPreview;
